/**
 * File Upload Security Utilities
 *
 * Validates file uploads for security: type checking, size limits,
 * dimension validation, filename sanitization, and malicious content detection.
 *
 * Main Functions:
 * ├── validateImageFile() ← Complete image validation and processing
 * └── detectMaliciousContent() ← Scans for hidden malicious content
 *
 * Utility Functions:
 * ├── validateFileType() ← MIME type and extension validation
 * ├── validateFileSize() ← File size checking
 * ├── sanitizeFilename() ← Safe filename creation
 * ├── validateImageDimensions() ← Pixel dimension validation
 * ├── getImageDimensions() ← Extract image dimensions
 * └── compressImage() ← Image compression and resizing
 */

import { FILE_UPLOAD_SECURITY } from '../constants/fileUpload.constants';
import type { ImageValidationResult } from '../models/fileUpload.model';

// Re-export types for external use
export type {
  FileValidationResult,
  ImageValidationResult,
} from '../models/fileUpload.model';

// =============================================================================
// UTILITY FUNCTIONS - Internal validation helpers
// =============================================================================

/**
 * Validates file type by checking MIME type and extension match
 */
export const validateFileType = (file: File): boolean => {
  const mimeType = file.type; // What the file claims to be
  const extension = '.' + file.name.split('.').pop()?.toLowerCase(); // File ending

  return Object.entries(FILE_UPLOAD_SECURITY.ALLOWED_IMAGE_TYPES).some(
    ([mime, exts]) =>
      mime === mimeType && (exts as readonly string[]).includes(extension),
  );
};

/**
 * Validates file size against maximum allowed size
 */
export const validateFileSize = (
  file: File,
  maxSize: number = FILE_UPLOAD_SECURITY.MAX_FILE_SIZE,
): boolean => {
  return file.size <= maxSize;
};

/**
 * Sanitizes filename by removing dangerous characters
 */
export const sanitizeFilename = (filename: string): string => {
  return filename
    .replace(/[^a-zA-Z0-9._-]/g, '') // Only allow safe characters
    .replace(/\.{2,}/g, '.') // Remove multiple dots
    .replace(/^\.+|\.+$/g, '') // Remove leading/trailing dots
    .slice(0, 255); // Limit length
};

/**
 * Validates image dimensions against maximum allowed size
 */
export const validateImageDimensions = (
  width: number,
  height: number,
  maxWidth: number = FILE_UPLOAD_SECURITY.MAX_IMAGE_DIMENSIONS.width,
  maxHeight: number = FILE_UPLOAD_SECURITY.MAX_IMAGE_DIMENSIONS.height,
): boolean => {
  return width <= maxWidth && height <= maxHeight && width > 0 && height > 0;
};

/**
 * Gets actual image dimensions by loading the image
 */
export const getImageDimensions = (
  file: File,
): Promise<{ width: number; height: number }> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);

    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve({ width: img.width, height: img.height });
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Invalid image file'));
    };

    img.src = url;
  });
};

/**
 * Compresses image to reduce file size and fit within dimensions
 */
export const compressImage = (
  file: File,
  maxWidth: number = FILE_UPLOAD_SECURITY.COMPRESSION_SETTINGS.maxWidth,
  maxHeight: number = FILE_UPLOAD_SECURITY.COMPRESSION_SETTINGS.maxHeight,
  quality: number = FILE_UPLOAD_SECURITY.COMPRESSION_SETTINGS.quality,
): Promise<string> => {
  return new Promise((resolve, reject) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();
    const url = URL.createObjectURL(file);

    if (!ctx) {
      reject(new Error('Canvas not supported'));
      return;
    }

    img.onload = () => {
      URL.revokeObjectURL(url);

      // Calculate new dimensions (keep aspect ratio)
      let { width, height } = img;

      if (width > height) {
        if (width > maxWidth) {
          height = (height * maxWidth) / width;
          width = maxWidth;
        }
      } else {
        if (height > maxHeight) {
          width = (width * maxHeight) / height;
          height = maxHeight;
        }
      }

      canvas.width = width;
      canvas.height = height;

      // Draw and compress
      ctx.drawImage(img, 0, 0, width, height);
      const compressedDataUrl = canvas.toDataURL('image/jpeg', quality);

      resolve(compressedDataUrl);
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Failed to load image'));
    };

    img.src = url;
  });
};

// =============================================================================
// CORE FUNCTIONS - Main file validation API
// =============================================================================

/**
 * Complete image file validation and processing
 *
 * Performs comprehensive security validation: file type, size, dimensions,
 * filename sanitization, and image compression. Main function for image uploads.
 */
export const validateImageFile = async (
  input: File | React.ChangeEvent<HTMLInputElement>,
): Promise<ImageValidationResult> => {
  try {
    // Handle both File objects and input events
    let file: File | undefined;

    if (input instanceof File) {
      file = input;
    } else if (input.target?.files?.[0]) {
      file = input.target.files[0];
    }

    if (!file) {
      return { isValid: false, error: 'No file provided' };
    }

    // Check file type
    if (!validateFileType(file)) {
      return {
        isValid: false,
        error: 'Invalid file type. Only JPG, PNG, and WebP images are allowed.',
      };
    }

    // Check file size
    if (!validateFileSize(file)) {
      return {
        isValid: false,
        error: `File size too large. Maximum size is ${FILE_UPLOAD_SECURITY.MAX_FILE_SIZE / (1024 * 1024)}MB.`,
      };
    }

    // Measure image dimensions
    let dimensions;
    try {
      dimensions = await getImageDimensions(file);
    } catch (error) {
      return { isValid: false, error: 'Invalid image file or corrupted data.' };
    }

    // Check if dimensions are acceptable
    if (!validateImageDimensions(dimensions.width, dimensions.height)) {
      return {
        isValid: false,
        error: `Image dimensions too large. Maximum size is ${FILE_UPLOAD_SECURITY.MAX_IMAGE_DIMENSIONS.width}x${FILE_UPLOAD_SECURITY.MAX_IMAGE_DIMENSIONS.height} pixels.`,
      };
    }

    // Compress image to manageable size
    let compressedDataUrl;
    try {
      compressedDataUrl = await compressImage(file);
    } catch (error) {
      return { isValid: false, error: 'Failed to process image.' };
    }

    // Create safe filename
    const sanitizedFilename = sanitizeFilename(file.name);
    const sanitizedFile = new File([file], sanitizedFilename, {
      type: file.type,
    });

    // All checks passed!
    return {
      isValid: true,
      sanitizedFile,
      dimensions,
      compressedDataUrl,
    };
  } catch (error) {
    return {
      isValid: false,
      error: 'Unexpected error during file validation.',
    };
  }
};

/**
 * Detects malicious content by scanning file content for suspicious patterns
 */
export const detectMaliciousContent = async (file: File): Promise<boolean> => {
  return new Promise((resolve) => {
    const reader = new FileReader();

    reader.onload = (e) => {
      const content = e.target?.result as string;

      // Use centralized malicious patterns for consistency
      const isMalicious = FILE_UPLOAD_SECURITY.FILE_MALICIOUS_PATTERNS.some(
        (pattern) => pattern.test(content),
      );

      resolve(isMalicious);
    };

    reader.onerror = () => resolve(false); // If can't read, assume safe

    // Read first 1KB to check for suspicious content
    const blob = file.slice(0, 1024);
    reader.readAsText(blob);
  });
};
